(function (globalScope) {
  'use strict';

  const DEFAULT_STORAGE_KEY = 'rebirth-lizzy-fundraiser-2026';
  const DEFAULT_REOPEN_HOURS = 24;
  const MAX_TIMEOUT = 2147483647;

  function getTimestamp(value) {
    if (value instanceof Date) return value.getTime();
    return new Date(value).getTime();
  }

  function isCampaignActive({ enabled, endAt, now = new Date() }) {
    if (!enabled) return false;

    const endTimestamp = Date.parse(endAt);
    const nowTimestamp = getTimestamp(now);

    return Number.isFinite(endTimestamp) && Number.isFinite(nowTimestamp) && nowTimestamp < endTimestamp;
  }

  function readDismissedAt(storage, key = DEFAULT_STORAGE_KEY) {
    try {
      if (!storage || typeof storage.getItem !== 'function') return null;

      const value = storage.getItem(key);
      if (value === null || value === '') return null;

      const timestamp = Number(value);
      return Number.isFinite(timestamp) ? timestamp : null;
    } catch (_error) {
      return null;
    }
  }

  function shouldOpenFundraiser({ dismissedAt, now = new Date(), reopenAfterHours = DEFAULT_REOPEN_HOURS }) {
    if (!Number.isFinite(dismissedAt)) return true;

    const nowTimestamp = getTimestamp(now);
    const hours = Number(reopenAfterHours);
    if (!Number.isFinite(nowTimestamp) || !Number.isFinite(hours) || hours <= 0) return true;

    return nowTimestamp - dismissedAt >= hours * 60 * 60 * 1000;
  }

  function writeDismissedAt(storage, key = DEFAULT_STORAGE_KEY, now = new Date()) {
    try {
      if (!storage || typeof storage.setItem !== 'function') return false;

      const timestamp = getTimestamp(now);
      if (!Number.isFinite(timestamp)) return false;

      storage.setItem(key, String(timestamp));
      return true;
    } catch (_error) {
      return false;
    }
  }

  const api = {
    isCampaignActive,
    readDismissedAt,
    shouldOpenFundraiser,
    writeDismissedAt,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (!globalScope?.customElements || !globalScope.HTMLElement) return;

  globalScope.RebirthFundraiserState = api;

  class RebirthFundraiserCampaign extends globalScope.HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
      this.reopenAfterHours = Number(this.dataset.reopenHours) || DEFAULT_REOPEN_HOURS;
      const serverNow = Number(this.dataset.serverNow) * 1000;
      this.clockOffset = Number.isFinite(serverNow) ? serverNow - Date.now() : 0;
      this.dialog = this.querySelector('[data-rebirth-fundraiser-dialog]');
      this.opener = this.querySelector('[data-rebirth-fundraiser-open]');
      this.closeButtons = Array.from(this.querySelectorAll('[data-rebirth-fundraiser-close]'));
      this.isHomepage = this.dataset.pageType === 'index';
      this.handleOpen = this.handleOpen.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);

      if (!this.isActive()) {
        this.deactivate();
        return;
      }

      this.hidden = false;
      this.attachListeners();
      this.scheduleExpiry();

      if (this.isHomepage && this.dialog && this.shouldAutoOpen()) {
        this.scheduleAutoOpen();
      }
    }

    disconnectedCallback() {
      this.detachListeners();
      this.cancelAutoOpen();
      globalScope.clearTimeout(this.expiryTimer);
      this.dataset.initialized = 'false';
    }

    isActive() {
      return isCampaignActive({
        enabled: this.dataset.enabled === 'true',
        endAt: this.dataset.endAt,
        now: this.getCampaignNow(),
      });
    }

    getCampaignNow() {
      return new Date(Date.now() + this.clockOffset);
    }

    shouldAutoOpen() {
      const url = new URL(globalScope.location.href);
      if (url.searchParams.get('fundraiser') === '1') {
        url.searchParams.delete('fundraiser');
        try {
          globalScope.history.replaceState(globalScope.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        } catch (_error) {
          // The dialog can still open when browser history is unavailable.
        }
        return true;
      }

      return shouldOpenFundraiser({
        dismissedAt: readDismissedAt(this.getStorage(), this.storageKey),
        now: this.getCampaignNow(),
        reopenAfterHours: this.reopenAfterHours,
      });
    }

    getStorage() {
      try {
        return globalScope.localStorage;
      } catch (_error) {
        return null;
      }
    }

    attachListeners() {
      if (this.isHomepage && this.opener && this.dialog) {
        this.opener.addEventListener('click', this.handleOpen);
      }

      this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
      this.dialog?.addEventListener('cancel', this.handleCancel);
      this.dialog?.addEventListener('click', this.handleBackdropClick);
    }

    detachListeners() {
      this.opener?.removeEventListener('click', this.handleOpen);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
      this.dialog?.removeEventListener('cancel', this.handleCancel);
      this.dialog?.removeEventListener('click', this.handleBackdropClick);
    }

    handleOpen(event) {
      event.preventDefault();
      this.openWhenConsentAllows();
    }

    handleCancel(event) {
      event.preventDefault();
      this.closeDialog(true);
    }

    handleBackdropClick(event) {
      if (event.target === this.dialog) this.closeDialog(true);
    }

    handleClose() {
      this.closeDialog(true);
    }

    scheduleAutoOpen() {
      if (globalScope.document.readyState === 'complete') {
        this.handleWindowLoad();
      } else {
        globalScope.addEventListener('load', this.handleWindowLoad, { once: true });
      }
    }

    handleWindowLoad() {
      this.autoOpenTimer = globalScope.setTimeout(() => this.openWhenConsentAllows(), 250);
    }

    hasVisiblePrivacyBanner() {
      const privacyBanner = globalScope.document.querySelector('#shopify-pc__banner');
      if (!privacyBanner) return false;

      const styles = globalScope.getComputedStyle(privacyBanner);
      return privacyBanner.getClientRects().length > 0 && styles.display !== 'none' && styles.visibility !== 'hidden';
    }

    openWhenConsentAllows() {
      if (!this.isActive()) return;

      if (!this.hasVisiblePrivacyBanner()) {
        this.consentObserver?.disconnect();
        this.consentObserver = null;
        globalScope.requestAnimationFrame(() => this.openDialog());
        return;
      }

      if (typeof globalScope.MutationObserver !== 'function' || this.consentObserver) return;

      this.consentObserver = new globalScope.MutationObserver(() => {
        if (!this.hasVisiblePrivacyBanner()) this.openWhenConsentAllows();
      });
      this.consentObserver.observe(globalScope.document.body, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
        childList: true,
        subtree: true,
      });

      if (!this.hasVisiblePrivacyBanner()) this.openWhenConsentAllows();
    }

    cancelAutoOpen() {
      globalScope.removeEventListener('load', this.handleWindowLoad);
      globalScope.clearTimeout(this.autoOpenTimer);
      this.consentObserver?.disconnect();
      this.consentObserver = null;
    }

    openDialog() {
      if (!this.dialog || !this.isActive() || this.dialog.open) return;

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    closeDialog(rememberDismissal) {
      if (!this.dialog?.open) return;
      if (rememberDismissal) writeDismissedAt(this.getStorage(), this.storageKey, this.getCampaignNow());

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
    }

    scheduleExpiry() {
      const endTimestamp = Date.parse(this.dataset.endAt);
      const delay = Math.min(Math.max(endTimestamp - this.getCampaignNow().getTime(), 0), MAX_TIMEOUT);

      this.expiryTimer = globalScope.setTimeout(() => {
        if (this.isActive()) {
          this.scheduleExpiry();
        } else {
          this.deactivate();
        }
      }, delay);
    }

    deactivate() {
      this.closeDialog(false);
      this.hidden = true;
      this.detachListeners();
      this.cancelAutoOpen();
      globalScope.clearTimeout(this.expiryTimer);
    }
  }

  if (!globalScope.customElements.get('rebirth-fundraiser-campaign')) {
    globalScope.customElements.define('rebirth-fundraiser-campaign', RebirthFundraiserCampaign);
  }
})(typeof window !== 'undefined' ? window : undefined);
